import { useId, useState, type ReactNode } from 'react';
import { NA_ATTESTATION_TEXT } from '../lib/checklist-status';
import { ConfirmDialog } from './confirm-dialog';

/**
 * The confirmation for marking a checklist item "doesn't apply" (ADR-0032). The
 * person reads the statement and ticks that it is true; only then does the
 * confirm button submit (`attest: true`). The statement is the seller's — when
 * the intermediary records it, the dialog says it is on the seller's behalf.
 */
export function NotApplicableAttestDialog({
  itemTitle,
  onBehalfOfSeller = false,
  pending = false,
  error,
  onConfirm,
  onCancel,
}: {
  itemTitle: string;
  /** The room's admin is recording the seller's statement. */
  onBehalfOfSeller?: boolean;
  pending?: boolean;
  /** Shown under the statement (e.g. the API's error). */
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const checkboxId = useId();
  return (
    <ConfirmDialog
      title="Doesn’t apply to the company?"
      confirmLabel="Confirm: doesn’t apply"
      pending={pending}
      confirmDisabled={!confirmed}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <p>
        You are marking <strong>“{itemTitle}”</strong> as not applicable
        {onBehalfOfSeller ? ', on the seller’s behalf' : ''}. It then counts as resolved with no
        document.
      </p>
      <blockquote className="border-l-2 border-line pl-3 text-ink">{NA_ATTESTATION_TEXT}</blockquote>
      <label htmlFor={checkboxId} className="flex items-start gap-2 text-ink">
        <input
          id={checkboxId}
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={confirmed}
          disabled={pending}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>
          {onBehalfOfSeller
            ? 'The seller has confirmed this statement is true.'
            : 'I confirm this statement is true.'}
        </span>
      </label>
      <p>It is recorded with your name and the time in the room’s audit log.</p>
      {error}
    </ConfirmDialog>
  );
}
